import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, ShieldCheckIcon, TargetIcon, SettingsIcon, FileTextIcon, MapPinIcon, PenToolIcon, UserIcon, BuildingIcon, BriefcaseIcon, ShoppingBagIcon, HeartPulseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function SeoVoorWebsitesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'SEO voor websites | Hoger scoren in Google | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie. Noord Digital helpt ondernemers in Noord-Holland met duurzame vindbaarheid.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'SEO voor websites | Hoger scoren in Google | Noord',
      'og:description': 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/seo-voor-websites',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het voordat SEO resultaat oplevert?',
    a: 'SEO is een langetermijnstrategie. De eerste verbeteringen zie je vaak binnen 3 maanden, maar de grootste resultaten komen na 6 tot 12 maanden consistent werken aan je vindbaarheid.'
  }, {
    q: 'Is SEO beter dan Google Ads?',
    a: 'Beide hebben hun plek. Google Ads levert direct verkeer maar stopt zodra je stopt met betalen. SEO bouwt duurzaam organisch verkeer op dat blijft groeien. Wij adviseren vaak een combinatie.'
  }, {
    q: 'Wat kost SEO optimalisatie?',
    a: 'De kosten hangen af van de huidige staat van je website, je concurrentie en je doelen. Wij bieden transparante SEO-pakketten en bespreken vooraf wat realistisch is voor jouw budget.'
  }, {
    q: 'Kan ik zelf aan SEO werken?',
    a: 'Basis SEO kun je zelf doen, maar voor technische optimalisatie, keyword strategie en structurele verbeteringen is specialistische kennis nodig. Wij combineren beide: wij doen het zware werk, jij leert de basics.'
  }, {
    q: 'Werkt SEO ook voor lokale bedrijven?',
    a: 'Juist voor lokale bedrijven is SEO enorm waardevol. Met lokale SEO zorg je dat klanten in jouw regio (Alkmaar, Noord-Holland) jou vinden wanneer ze zoeken naar jouw diensten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1676" data-mp="2v0mlvdargk63">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1677" data-mp="2uys2w4j1v24n">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1678" data-mp="2zsmgcq82akva">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1679" data-mp="33tg67p52e9qt" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1680" data-mp="38t2jfykw7pzm" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1681" data-mp="3drm3tuju8tpv" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1682" data-mp="39svme09drj24">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1683" data-mp="34vvqgi68vwoq">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1684" data-mp="2h5roef8pg4zu">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1685" data-mp="2r3yzwik85uug">/</span>
            <span className="text-accent-light font-medium" data-id="element-1686" data-mp="2r1md9s3iuwb2">
              SEO voor websites
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1687" data-mp="6d81fcofwe8f">
            SEO voor websites — hoger scoren in Google
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1688" data-mp="1gq24sbvp83h">
            Meer organisch verkeer, betere vindbaarheid en meer klanten. Wij
            optimaliseren jouw website voor zoekmachines.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1689" data-mp="3w3x7lvih2do5">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1690" data-mp="3yjlsg79ic3t">
              Plan een SEO-gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-1691" data-mp="ivaqn4zpgg0t" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom SEO belangrijk is */}
      <section className="py-24 bg-surface-muted" data-id="element-1692" data-mp="1an9u0cf6el3h">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1693" data-mp="1amgkj3ou1hhn">
          <div className="text-center mb-16" data-id="element-1694" data-mp="1fei317clg4g4">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1695" data-mp="1ff9lrhl3e7oz">
              Waarom SEO belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1696" data-mp="1l8deondc29x1">
              Zoekmachine optimalisatie is de meest duurzame manier om online te
              groeien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1697" data-mp="1bafse5f1an72">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1698" data-mp="1bcjz0v4bi0b1">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1699" data-mp="11ke69j68reus">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-1700" data-mp="xij4kdkeezgh" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1701" data-mp="sjod6n8d82kr">
                Meer organisch verkeer
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1702" data-mp="nl4seq5icwtz">
                Gratis bezoekers via Google die actief zoeken naar jouw diensten
                of producten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1703" data-mp="8iczzeacmr49">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1704" data-mp="dfn0boza0n6v">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-1705" data-mp="ih2kzfxjkroq" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1706" data-mp="nf44u32skv19">
                Hogere Google posities
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1707" data-mp="3ly17rxsdau6w">
                Verschijn bovenaan de zoekresultaten voor relevante zoekwoorden
                in jouw branche.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1708" data-mp="3vtco2kjfc9gr">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1709" data-mp="4i2vy764aas1">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-1710" data-mp="333vx7rbgd0mn" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1711" data-mp="3838nyn5g2rui">
                Meer vertrouwen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1712" data-mp="q7ys0x62q3h4">
                Hoge Google posities stralen autoriteit en betrouwbaarheid uit
                naar potentiële klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1713" data-mp="v5i7u1if6ob6">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1714" data-mp="194hvp6y3yz6a">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-1715" data-mp="1547r9ypysgtj" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1716" data-mp="1iujlq4a0urnu">
                Duurzaam resultaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1717" data-mp="1dvgfigib2fea">
                SEO levert langdurig resultaat op, ook als je stopt met
                investeren in advertenties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze SEO aanpak */}
      <section className="py-24 bg-white" data-id="element-1718" data-mp="27f66ckqu6lim">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1719" data-mp="27g7h0o3xx5zt">
          <div className="text-center mb-16" data-id="element-1720" data-mp="2bhkpz72bxpay">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1721" data-mp="2bf6uix3qmb2v">
              Onze SEO aanpak
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1722" data-mp="22gmkslg5nbqs">
              Een bewezen strategie voor betere vindbaarheid en meer conversie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1723" data-mp="1npvpv4j9nwjg">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1724" data-mp="1sj8bd5snfn7z">
              <div className="flex items-start gap-4" data-id="element-1725" data-mp="1esv7beivosnx">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1726" data-mp="1jplhxmbwyw1y">
                  <SettingsIcon className="w-6 h-6 text-navy-700" data-id="element-1727" data-mp="14tvwpqejymya" />
                </div>
                <div data-id="element-1728" data-mp="19s76k14ukxgi">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1729" data-mp="19qmd6eu9rkk8">
                    Technische SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1730" data-mp="jx6397yqjlhl">
                    Snelle laadtijden, mobiel-vriendelijk, schone code, XML
                    sitemaps en gestructureerde data voor een perfecte basis.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1731" data-mp="ew8q59sl1hmy">
              <div className="flex items-start gap-4" data-id="element-1732" data-mp="12nnut97u0b1d">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1733" data-mp="17jmdcg6haytv">
                  <FileTextIcon className="w-6 h-6 text-navy-700" data-id="element-1734" data-mp="sv00qm88kl6c" />
                </div>
                <div data-id="element-1735" data-mp="xtt4vo1muztu">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1736" data-mp="xt1j14xu8ggr">
                    On-page SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1737" data-mp="1lim5hxt79vus">
                    Keyword research, meta-tags, koppen structuur, interne
                    linking en content optimalisatie voor maximale relevantie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1738" data-mp="360dfay3425u2">
              <div className="flex items-start gap-4" data-id="element-1739" data-mp="3kt6sudytobw3">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1740" data-mp="3ftlvg7zawblh">
                  <MapPinIcon className="w-6 h-6 text-navy-700" data-id="element-1741" data-mp="2s389mz7b78zc" />
                </div>
                <div data-id="element-1742" data-mp="2n4x0kqou92i6">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1743" data-mp="2n5gbvojzgsn8">
                    Lokale SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1744" data-mp="2x1t22cgbkod8">
                    Google Mijn Bedrijf, lokale zoekwoorden, NAW-consistentie en
                    reviews strategie om lokaal te domineren.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1745" data-mp="cbambf3c97ab">
              <div className="flex items-start gap-4" data-id="element-1746" data-mp="358eyahv4dblu">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1747" data-mp="3a42kqpi7vqnn">
                  <PenToolIcon className="w-6 h-6 text-navy-700" data-id="element-1748" data-mp="3f4qe3htx4jl1" />
                </div>
                <div data-id="element-1749" data-mp="3j4ips3elz5um">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1750" data-mp="3j50rvgnyceag">
                    Content strategie
                  </h3>
                  <p className="text-slate-600" data-id="element-1751" data-mp="2ree8idm5ayxm">
                    Kennisbank artikelen, blogs en landingspagina's die ranken
                    op relevante zoektermen en bezoekers overtuigen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO resultaten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1752" data-mp="23ojwtzdkbgey">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1753" data-mp="27pveniq72xcl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1754" data-mp="1v685svu5kx14">
          <div className="text-center mb-16" data-id="element-1755" data-mp="1r4wdgijw9xp8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1756" data-mp="1m7mpl7c5lu3i">
              SEO resultaten die je mag verwachten
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1757" data-mp="1h9uobmpmcvmn">
              Meetbare groei voor jouw bedrijf door doelgerichte optimalisatie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1758" data-mp="17ji6415a1ejo">
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1759" data-mp="13gv79ga6utcz">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1760" data-mp="yhscqpru45j9">
                Top 10 posities
              </div>
              <p className="text-navy-200" data-id="element-1761" data-mp="tifj9m6gk07p">
                Voor relevante zoekwoorden in jouw regio
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1762" data-mp="2tlqgecnoofoq">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1763" data-mp="2yj8fzcft9ik2">
                +200% organisch verkeer
              </div>
              <p className="text-navy-200" data-id="element-1764" data-mp="2ou8dvk60i71n">Gemiddelde groei binnen 6 maanden</p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1765" data-mp="2augt90kzpseo">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1766" data-mp="2ew2eb97ohczy">
                Meer leads
              </div>
              <p className="text-navy-200" data-id="element-1767" data-mp="20wsxq8fk8gvg">
                Bezoekers die actief op zoek zijn naar jouw dienst
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie is SEO geschikt? */}
      <section className="py-24 bg-surface-muted" data-id="element-1768" data-mp="3vbltc7gg17ne">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1769" data-mp="43zgbo7u3stv">
          <div className="text-center mb-16" data-id="element-1770" data-mp="437xzf38zrlj">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1771" data-mp="1oligd5gx5dm6">
              Voor wie is SEO geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1772" data-mp="1jo9zr9bf5hn1">
              Onze SEO-diensten zijn effectief voor diverse soorten
              ondernemingen.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-1773" data-mp="1tkcz67evz26a">
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1774" data-mp="27c05l02jlr2s">
              <MapPinIcon className="w-5 h-5 text-accent" data-id="element-1775" data-mp="22dycdmbekbdl" />
              <span className="font-medium text-navy-900" data-id="element-1776" data-mp="22fj7pe5jjyci">
                Lokale ondernemers
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1777" data-mp="2cbw088pco65o">
              <BuildingIcon className="w-5 h-5 text-accent" data-id="element-1778" data-mp="mweohrk4r0zj" />
              <span className="font-medium text-navy-900" data-id="element-1779" data-mp="mwy77ahlttem">MKB bedrijven</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1780" data-mp="hzfx3grup851">
              <UserIcon className="w-5 h-5 text-accent" data-id="element-1781" data-mp="17njxye5f5em7" />
              <span className="font-medium text-navy-900" data-id="element-1782" data-mp="1bmstnok0rmke">ZZP'ers</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1783" data-mp="1gmoyfq0jji10">
              <ShoppingBagIcon className="w-5 h-5 text-accent" data-id="element-1784" data-mp="1ll8lxuus91vh" />
              <span className="font-medium text-navy-900" data-id="element-1785" data-mp="1ljf9fyxhnnfk">Webshops</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1786" data-mp="1qkcsseu8inin">
              <BriefcaseIcon className="w-5 h-5 text-accent" data-id="element-1787" data-mp="1ukdhqteymh3y" />
              <span className="font-medium text-navy-900" data-id="element-1788" data-mp="1zcn3dx91kv0f">Dienstverleners</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1789" data-mp="29bcnzxsbotkd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1790" data-mp="298zwaswrj4u4">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1791" data-mp="2eap6y7g2pgho">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1792" data-mp="2o57sgswti60">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1793" data-mp="3tsvhew6wjg3p">
              WordPress website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1794" data-mp="3jws5wzq5u543">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1795" data-mp="lca8ffe9sn0l">
              Wat kost een website?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1796" data-mp="blp8nomv1cg3">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1797" data-mp="6ndytdwkf1xv">
              Webdesign bureau Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1798" data-mp="zbrxlvvha2ah">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1799" data-mp="7ogswcwrxmr6">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1800" data-mp="3nok1krtdjvf">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1801" data-mp="3o6eqcytvq18">
          <div className="text-center mb-16" data-id="element-1802" data-mp="rd96idn6ez9g">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1803" data-mp="rbeigqglyr15">
              Veelgestelde vragen over SEO
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1804" data-mp="he9v4nb10pir">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-1805" data-mp="mdwj9rse7q6c" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1806" data-mp="323z9z0bqcbbg">
                  <span className="font-bold text-navy-900" data-id="element-1807" data-mp="2zjkub42qblla">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1808" data-mp="2uo5fo9wwdlnt" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1809" data-mp="38jg71dbxnonf" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1810" data-mp="2fxoix3v7jjx9">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1811" data-mp="2kwhu2pn0uyu9" />
    </main>;
}